import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Table,
  LayerCard,
  Empty,
  Badge,
  Button,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { BuildingsIcon, HouseIcon, PlusIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { CompanyLogo } from '../components/CompanyLogo'
import { useHasRole } from '../auth/useRoles'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { Company } from '@hub-wifi/shared'

/* ------------------------------------------------------------------ */
/*  CompaniesPage                                                      */
/* ------------------------------------------------------------------ */

export function CompaniesPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const isAdmin = useHasRole('admin')
  const navigate = useNavigate()
  const toasts = useKumoToastManager()
  const [companies, setCompanies] = useState<Company[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const loadCompanies = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const data = await apiRequest<Company[]>('/api/companies', { token })
      setCompanies(data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently])

  useEffect(() => {
    void loadCompanies()
  }, [loadCompanies])

  async function handleStatusToggle(company: Company) {
    const isActive = company.status === 'active'
    const confirmMessage = isActive
      ? t('companies.confirmDeactivate')
      : t('companies.confirmActivate')
    if (!window.confirm(confirmMessage)) return

    try {
      const token = await getAccessTokenSilently()
      if (isActive) {
        await apiRequest(`/api/companies/${company.id}`, {
          method: 'DELETE',
          token,
        })
      } else {
        await apiRequest(`/api/companies/${company.id}`, {
          method: 'PUT',
          token,
          body: { status: 'active' },
        })
      }
      toasts.add({ variant: 'success', title: t('companies.statusUpdateSuccess') })
      setLoading(true)
      await loadCompanies()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    }
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('companies.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('companies.title')}
        description={t('companies.description')}
      >
        {isAdmin && (
          <Button
            variant="primary"
            size="sm"
            icon={<PlusIcon size={16} />}
            onClick={() => navigate('/companies/new')}
          >
            {t('companies.addCompany')}
          </Button>
        )}
      </PageHeader>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error === 'GENERIC_ERROR' ? t('common.error') : error}
        </Banner>
      )}

      {!loading && !error && companies.length === 0 && (
        <Empty
          icon={<BuildingsIcon size={48} className="text-kumo-inactive" />}
          title={t('companies.empty')}
        />
      )}

      {!loading && !error && companies.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>{t('companies.logo')}</Table.Head>
                  <Table.Head>{t('companies.name')}</Table.Head>
                  <Table.Head>{t('companies.taxId')}</Table.Head>
                  <Table.Head>{t('companies.domains')}</Table.Head>
                  <Table.Head>{t('companies.status')}</Table.Head>
                  <Table.Head>{t('companies.email')}</Table.Head>
                  {isAdmin && <Table.Head>{t('companies.actions')}</Table.Head>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {companies.map((company) => (
                  <Table.Row key={company.id}>
                    <Table.Cell>
                      <CompanyLogo domain={(company.domains ?? [])[0]} size={28} />
                    </Table.Cell>
                    <Table.Cell>{company.name}</Table.Cell>
                    <Table.Cell>{company.taxId}</Table.Cell>
                    <Table.Cell>
                      <div className="flex flex-wrap gap-1">
                        {(company.domains ?? []).map((domain) => (
                          <Badge key={domain} variant="secondary">
                            {domain}
                          </Badge>
                        ))}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant={company.status === 'active' ? 'primary' : 'secondary'}>
                        {company.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>{company.email}</Table.Cell>
                    {isAdmin && (
                      <Table.Cell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<PencilSimpleIcon size={16} />}
                            onClick={() => navigate(`/companies/${company.id}/edit`)}
                          >
                            {t('companies.edit')}
                          </Button>
                          <Button
                            variant={
                              company.status === 'active' ? 'secondary-destructive' : 'secondary'
                            }
                            size="sm"
                            onClick={() => void handleStatusToggle(company)}
                          >
                            {company.status === 'active'
                              ? t('companies.deactivate')
                              : t('companies.activate')}
                          </Button>
                        </div>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}
